import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Calendar, Trash2, Plus, RefreshCw, RotateCcw, Users, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { recurringEventService, RecurringEventPattern, EventInstance } from '@/services/recurringEventService';
import { GuestManagement } from '@/components/GuestManagement';
import { GuestContactSelector } from './GuestContactSelector';
import { FamilyMemberSelector } from './FamilyMemberSelector';

interface NewRecurringEvent {
  name: string;
  description: string;
  event_type: 'voting' | 'rsvp';
  rsvp_date: string;
  rsvp_time: string;
  rsvp_location: string;
  pattern_type: 'daily' | 'weekly' | 'monthly' | 'yearly';
  pattern_interval: number;
  pattern_days: number[];
  end_date: string;
  max_occurrences: number;
  default_guests: Array<{
    email?: string;
    phone?: string;
    name?: string;
  }>;
  guest_inheritance_type: 'all_instances' | 'per_instance';
}

const RecurringEventsManager = () => {
  const { user } = useAuth();
  const [recurringEvents, setRecurringEvents] = useState<RecurringEventPattern[]>([]);
  const [eventInstances, setEventInstances] = useState<{ [key: string]: EventInstance[] }>({});
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const [newEvent, setNewEvent] = useState<NewRecurringEvent>({
    name: '',
    description: '',
    event_type: 'rsvp',
    rsvp_date: '',
    rsvp_time: '',
    rsvp_location: '',
    pattern_type: 'weekly',
    pattern_interval: 1,
    pattern_days: [],
    end_date: '',
    max_occurrences: 0,
    default_guests: [],
    guest_inheritance_type: 'all_instances',
  });

  useEffect(() => {
    if (user) {
      loadRecurringEvents();
    }
  }, [user]);

  const loadRecurringEvents = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const events = await recurringEventService.getRecurringEvents();
      setRecurringEvents(events);

      // Load instances for each recurring event
      const instancesMap: { [key: string]: EventInstance[] } = {};
      for (const event of events) {
        if (event.event?.id) {
          try {
            const instances = await recurringEventService.getEventInstances(event.event.id);
            instancesMap[event.event.id] = instances;
          } catch (error) {
            console.error(`Error loading instances for event ${event.event.id}:`, error);
          }
        }
      }
      setEventInstances(instancesMap);
    } catch (error) {
      console.error('Error loading recurring events:', error);
      toast.error('Failed to load recurring events');
    } finally {
      setLoading(false);
    }
  };

  const createRecurringEvent = async () => {
    if (!user) return;

    try {
      await recurringEventService.createRecurringEvent(newEvent);
      
      toast.success('Recurring event created successfully!');
      setIsDialogOpen(false);
      setNewEvent({
        name: '',
        description: '',
        event_type: 'rsvp',
        rsvp_date: '',
        rsvp_time: '',
        rsvp_location: '',
        pattern_type: 'weekly',
        pattern_interval: 1,
        pattern_days: [],
        end_date: '',
        max_occurrences: 0,
        default_guests: [],
        guest_inheritance_type: 'all_instances',
      });
      loadRecurringEvents();
    } catch (error) {
      console.error('Error creating recurring event:', error);
      toast.error('Failed to create recurring event');
    }
  };

  const deleteRecurringEvent = async (eventId: string) => {
    if (window.confirm('Are you sure you want to delete this recurring event? This will also delete all future instances.')) {
      try {
        await recurringEventService.deleteRecurringEvent(eventId);
        toast.success('Recurring event deleted successfully');
        loadRecurringEvents();
      } catch (error) {
        console.error('Error deleting recurring event:', error);
        toast.error('Failed to delete recurring event');
      }
    }
  };

  const toggleInstanceStatus = async (instanceId: string, isActive: boolean) => {
    try {
      await recurringEventService.toggleInstanceStatus(instanceId, isActive);
      toast.success(`Instance ${isActive ? 'activated' : 'deactivated'} successfully`);
      loadRecurringEvents();
    } catch (error) {
      console.error('Error toggling instance status:', error);
      toast.error('Failed to update instance status');
    }
  };

  const formatPattern = (event: RecurringEventPattern): string => {
    const { pattern_type, pattern_interval, pattern_days, end_date, max_occurrences } = event;
    
    let pattern = `Every `;
    
    if (pattern_interval > 1) {
      pattern += `${pattern_interval} `;
    }
    
    switch (pattern_type) {
      case 'daily':
        pattern += pattern_interval === 1 ? 'day' : 'days';
        break;
      case 'weekly':
        pattern += pattern_interval === 1 ? 'week' : 'weeks';
        if (pattern_days && pattern_days.length > 0) {
          const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const selectedDays = pattern_days.map(day => days[day]).join(', ');
          pattern += ` on ${selectedDays}`;
        }
        break;
      case 'monthly':
        pattern += pattern_interval === 1 ? 'month' : 'months';
        break;
      case 'yearly':
        pattern += pattern_interval === 1 ? 'year' : 'years';
        break;
    }
    
    if (end_date) {
      pattern += ` until ${new Date(end_date).toLocaleDateString()}`;
    } else if (max_occurrences) {
      pattern += ` for ${max_occurrences} occurrences`;
    }
    
    return pattern;
  };

  const toggleWeekday = (day: number) => {
    setNewEvent(prev => ({
      ...prev,
      pattern_days: prev.pattern_days.includes(day)
        ? prev.pattern_days.filter(d => d !== day)
        : [...prev.pattern_days, day].sort()
    }));
  };

  if (loading) {
    return <div className="text-center py-8">Loading recurring events...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Recurring Events
          </CardTitle>
          <CardDescription>
            Create and manage events that repeat automatically with guest management
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Create Recurring Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Recurring Event</DialogTitle>
                <DialogDescription>
                  Set up an event that repeats automatically with guest management.
                </DialogDescription>
              </DialogHeader>
              
              <Tabs defaultValue="event" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="event">Event Details</TabsTrigger>
                  <TabsTrigger value="pattern">Recurrence Pattern</TabsTrigger>
                  <TabsTrigger value="guests">Guest Management</TabsTrigger>
                </TabsList>
                
                <TabsContent value="event" className="space-y-4">
                  <div>
                    <Label htmlFor="name">Event Name</Label>
                    <Input
                      id="name"
                      value={newEvent.name}
                      onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                      placeholder="Enter event name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                      placeholder="Enter event description"
                    />
                  </div>

                  <div>
                    <Label htmlFor="event_type">Event Type</Label>
                    <Select value={newEvent.event_type} onValueChange={(value: 'voting' | 'rsvp') => setNewEvent({ ...newEvent, event_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rsvp">RSVP Event</SelectItem>
                        <SelectItem value="voting">Voting Event</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {newEvent.event_type === 'rsvp' && (
                    <>
                      <div>
                        <Label htmlFor="rsvp_date">Event Date</Label>
                        <Input
                          id="rsvp_date"
                          type="date"
                          value={newEvent.rsvp_date}
                          onChange={(e) => setNewEvent({ ...newEvent, rsvp_date: e.target.value })}
                        />
                      </div>

                      <div>
                        <Label htmlFor="rsvp_time">Event Time</Label>
                        <Input
                          id="rsvp_time"
                          type="time"
                          value={newEvent.rsvp_time}
                          onChange={(e) => setNewEvent({ ...newEvent, rsvp_time: e.target.value })}
                        />
                      </div>

                      <div>
                        <Label htmlFor="rsvp_location">Location</Label>
                        <Input
                          id="rsvp_location"
                          value={newEvent.rsvp_location}
                          onChange={(e) => setNewEvent({ ...newEvent, rsvp_location: e.target.value })}
                          placeholder="Enter event location"
                        />
                      </div>
                    </>
                  )}
                </TabsContent>
                
                <TabsContent value="pattern" className="space-y-4">
                  <div>
                    <Label htmlFor="pattern_type">Repeat Pattern</Label>
                    <Select value={newEvent.pattern_type} onValueChange={(value: 'daily' | 'weekly' | 'monthly' | 'yearly') => setNewEvent({ ...newEvent, pattern_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="pattern_interval">Repeat Every</Label>
                    <Input
                      id="pattern_interval"
                      type="number"
                      min="1"
                      value={newEvent.pattern_interval}
                      onChange={(e) => setNewEvent({ ...newEvent, pattern_interval: parseInt(e.target.value) || 1 })}
                    />
                  </div>

                  {newEvent.pattern_type === 'weekly' && (
                    <div>
                      <Label>Days of the Week</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                          <div key={day} className="flex items-center space-x-2">
                            <Checkbox
                              id={`day-${index}`}
                              checked={newEvent.pattern_days.includes(index)}
                              onCheckedChange={() => toggleWeekday(index)}
                            />
                            <Label htmlFor={`day-${index}`} className="text-sm">{day}</Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="end_date">End Date (Optional)</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={newEvent.end_date}
                      onChange={(e) => setNewEvent({ ...newEvent, end_date: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="max_occurrences">Max Occurrences (Optional)</Label>
                    <Input
                      id="max_occurrences"
                      type="number"
                      min="1"
                      value={newEvent.max_occurrences || ''}
                      onChange={(e) => setNewEvent({ ...newEvent, max_occurrences: parseInt(e.target.value) || 0 })}
                      placeholder="Leave empty for unlimited"
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="guests" className="space-y-4">
                  <div>
                    <Label>Guest Inheritance</Label>
                    <Select 
                      value={newEvent.guest_inheritance_type} 
                      onValueChange={(value: 'all_instances' | 'per_instance') => setNewEvent({ ...newEvent, guest_inheritance_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_instances">Same guests for all instances</SelectItem>
                        <SelectItem value="per_instance">Manage guests per instance</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground mt-1">
                      {newEvent.guest_inheritance_type === 'all_instances' 
                        ? 'These guests will be automatically invited to all recurring event instances.'
                        : 'You can manage guests individually for each event instance.'}
                    </p>
                  </div>

                  <div>
                    <Label>Default Guests</Label>
                    <Tabs defaultValue="saved" className="w-full mt-2">
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="saved">Saved Contacts</TabsTrigger>
                        <TabsTrigger value="family">Family Members</TabsTrigger>
                        <TabsTrigger value="manual">Manual Entry</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="saved" className="space-y-4">
                        <GuestContactSelector 
                          selectedGuests={newEvent.default_guests.map(g => ({
                            id: crypto.randomUUID(),
                            name: g.name || '',
                            email: g.email || '',
                            phone: g.phone || '',
                            hasVoted: false,
                            invitedAt: new Date().toISOString(),
                          }))}
                          onGuestsChange={(updatedGuests) => {
                            setNewEvent({
                              ...newEvent,
                              default_guests: updatedGuests.map(g => ({
                                name: g.name,
                                email: g.email,
                                phone: g.phone,
                              }))
                            });
                          }}
                        />
                      </TabsContent>

                      <TabsContent value="family" className="space-y-4">
                        <FamilyMemberSelector
                          selectedGuests={newEvent.default_guests.map(g => ({
                            id: crypto.randomUUID(),
                            name: g.name || '',
                            email: g.email || '',
                            phone: g.phone || '',
                            hasVoted: false,
                            invitedAt: new Date().toISOString(),
                          }))}
                          onGuestsChange={(updatedGuests) => {
                            setNewEvent({
                              ...newEvent,
                              default_guests: updatedGuests.map(g => ({
                                name: g.name,
                                email: g.email,
                                phone: g.phone,
                              }))
                            });
                          }}
                        />
                      </TabsContent>

                      <TabsContent value="manual" className="space-y-4">
                        <div className="space-y-2">
                          {newEvent.default_guests.map((guest, index) => (
                            <div key={index} className="flex gap-2 items-center p-2 border rounded">
                              <Input
                                placeholder="Name"
                                value={guest.name || ''}
                                onChange={(e) => {
                                  const updatedGuests = [...newEvent.default_guests];
                                  updatedGuests[index] = { ...guest, name: e.target.value };
                                  setNewEvent({ ...newEvent, default_guests: updatedGuests });
                                }}
                              />
                              <Input
                                placeholder="Email"
                                value={guest.email || ''}
                                onChange={(e) => {
                                  const updatedGuests = [...newEvent.default_guests];
                                  updatedGuests[index] = { ...guest, email: e.target.value };
                                  setNewEvent({ ...newEvent, default_guests: updatedGuests });
                                }}
                              />
                              <Input
                                placeholder="Phone"
                                value={guest.phone || ''}
                                onChange={(e) => {
                                  const updatedGuests = [...newEvent.default_guests];
                                  updatedGuests[index] = { ...guest, phone: e.target.value };
                                  setNewEvent({ ...newEvent, default_guests: updatedGuests });
                                }}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const updatedGuests = newEvent.default_guests.filter((_, i) => i !== index);
                                  setNewEvent({ ...newEvent, default_guests: updatedGuests });
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            onClick={() => {
                              setNewEvent({
                                ...newEvent,
                                default_guests: [...newEvent.default_guests, { name: '', email: '', phone: '' }]
                              });
                            }}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Guest
                          </Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={createRecurringEvent}>
                  Create Recurring Event
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {recurringEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <RotateCcw className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="mb-2">No recurring events yet</p>
              <p className="text-sm">Create your first recurring event to get started!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recurringEvents.map((event) => (
                <div key={event.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{event.event?.name}</h3>
                        <Badge variant="secondary">
                          <Users className="h-3 w-3 mr-1" />
                          {event.default_guests.length} default guests
                        </Badge>
                      </div>
                      {event.event?.description && (
                        <p className="text-sm text-muted-foreground mt-1">{event.event.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <RefreshCw className="h-3 w-3" />
                          {formatPattern(event)}
                        </span>
                        {event.event?.rsvp_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Starting {new Date(event.event.rsvp_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      
                      {/* Show instances */}
                      {event.event?.id && eventInstances[event.event.id] && (
                        <div className="mt-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedEventId(selectedEventId === event.event!.id ? null : event.event!.id)}
                            className="text-xs"
                          >
                            {selectedEventId === event.event.id ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                            {selectedEventId === event.event.id ? 'Hide' : 'Show'} Instances ({eventInstances[event.event.id].length})
                          </Button>
                          
                          {selectedEventId === event.event.id && (
                            <div className="ml-4 mt-2 space-y-2">
                              {eventInstances[event.event.id].slice(0, 5).map((instance) => (
                                <div key={instance.id} className="flex items-center justify-between text-sm border-l-2 border-muted pl-3">
                                  <div>
                                    <span className="font-medium">#{instance.occurrence_number}</span>
                                    <span className="mx-2">•</span>
                                    <span>{new Date(instance.instance_date).toLocaleDateString()}</span>
                                    <span className="mx-2">•</span>
                                    <span className="text-muted-foreground">{instance.guest_count} guests</span>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleInstanceStatus(instance.id, !instance.is_active)}
                                    className={instance.is_active ? 'text-green-600' : 'text-muted-foreground'}
                                  >
                                    {instance.is_active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                                  </Button>
                                </div>
                              ))}
                              {eventInstances[event.event.id].length > 5 && (
                                <p className="text-xs text-muted-foreground ml-3">
                                  And {eventInstances[event.event.id].length - 5} more instances...
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteRecurringEvent(event.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RecurringEventsManager;